import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  api,
  type CreditActivityGrantItem,
  type CreditActivityRefundItem,
  type CreditActivityPerson,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const PAGE_SIZE = 25;

type PeriodDays = number | undefined;
const PERIODS: { label: string; days: PeriodDays }[] = [
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: 'All Time', days: undefined },
];

function person(p: CreditActivityPerson | null): string {
  if (!p) return 'unknown (legacy)';
  return p.username ?? p.displayName ?? p.userId;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * One paginated activity table. `kind` selects grants (admin grants +
 * deductions) or refunds (admin-processed Stripe refunds). Both show who acted,
 * who was affected, the amount, and when.
 */
function ActivitySection({ kind, days }: { kind: 'grants' | 'refunds'; days: PeriodDays }) {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.adminCredits.activity(kind, { offset, days: days ?? 'all' }),
    queryFn: () => api.adminGetCreditActivity({ kind, limit: PAGE_SIZE, offset, days }),
  });

  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          {kind === 'grants' ? 'Grants & Deductions' : 'Refunds'}
        </h2>
        <span className="text-xs text-muted-foreground">{total} total</span>
      </div>

      {isError && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load activity'}
        </p>
      )}

      {isLoading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-8 bg-muted rounded" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No {kind} in this period.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 font-medium">Date</th>
                {kind === 'grants' && <th className="py-2 font-medium">Action</th>}
                <th className="py-2 font-medium">Admin</th>
                <th className="py-2 font-medium">Target user</th>
                <th className="py-2 font-medium">Amount</th>
                {kind === 'grants' && <th className="py-2 font-medium">Bucket</th>}
                <th className="py-2 font-medium">
                  {kind === 'grants' ? 'Reason' : 'Reason / Payment'}
                </th>
              </tr>
            </thead>
            <tbody>
              {kind === 'grants'
                ? (items as CreditActivityGrantItem[]).map((it) => (
                    <tr key={it.transactionId} className="border-b border-border/50">
                      <td className="py-2 whitespace-nowrap text-muted-foreground">
                        {formatDate(it.createdAt)}
                      </td>
                      <td className="py-2">
                        <span
                          className={
                            it.kind === 'deduct'
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-green-600 dark:text-green-400'
                          }
                        >
                          {it.kind === 'deduct' ? 'Deduct' : 'Grant'}
                        </span>
                      </td>
                      <td className="py-2 text-foreground">{person(it.actor)}</td>
                      <td className="py-2">
                        <div className="text-foreground">{person(it.target)}</div>
                        <div className="text-muted-foreground font-mono text-xs">
                          {it.target?.userId}
                        </div>
                      </td>
                      <td className="py-2 font-mono text-foreground">{it.display}</td>
                      <td className="py-2 text-muted-foreground">{it.bucket ?? '—'}</td>
                      <td className="py-2 text-muted-foreground">
                        {it.reason ?? it.description ?? '—'}
                      </td>
                    </tr>
                  ))
                : (items as CreditActivityRefundItem[]).map((it) => (
                    <tr key={it.paymentId} className="border-b border-border/50">
                      <td className="py-2 whitespace-nowrap text-muted-foreground">
                        {formatDate(it.createdAt)}
                      </td>
                      <td className="py-2 text-foreground">{person(it.actor)}</td>
                      <td className="py-2">
                        <div className="text-foreground">{person(it.target)}</div>
                        <div className="text-muted-foreground font-mono text-xs">
                          {it.target?.userId}
                        </div>
                      </td>
                      <td className="py-2 font-mono text-foreground">{it.display}</td>
                      <td className="py-2 text-muted-foreground">
                        <div>{it.status}</div>
                        <div className="font-mono text-xs">{it.paymentId}</div>
                        {it.reason && <div className="text-xs">{it.reason}</div>}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={offset === 0}
            className="px-3 py-1.5 rounded-md bg-muted text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-muted-foreground">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            className="px-3 py-1.5 rounded-md bg-muted text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Dev-console "Credit Activity" page — a full audit view of admin credit grants,
 * deductions, and refunds (who acted, who was affected, amount, date). Distinct
 * from the per-user view in the Payments dashboard: this is platform-wide.
 */
export function CreditActivityPage() {
  const [days, setDays] = useState<PeriodDays>(30);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dev Console
        </Link>
        <h1 className="text-3xl font-bold text-foreground">Credit Activity</h1>
        <p className="mt-2 text-muted-foreground">
          Platform-wide log of admin credit grants, deductions, and refunds.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {PERIODS.map((p) => (
          <button
            key={p.label}
            onClick={() => setDays(p.days)}
            className={`px-3 py-1.5 text-sm rounded-md ${
              days === p.days
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="space-y-8">
        <ActivitySection kind="grants" days={days} />
        <ActivitySection kind="refunds" days={days} />
      </div>
    </div>
  );
}

export default CreditActivityPage;
